/**
 * Guide Reviews Page
 *
 * Allows guides to view and respond to their reviews with filtering capabilities.
 * Displays reviews with customer info, ratings, and inline response composer.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: 2.2 - Guide Reviews Page
 *
 * @version 1.0
 * @created 2026-01-17
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import EmptyState from '../../components/common/EmptyState';
import {
  Star as _Star,
  MessageSquare,
  User as _User,
  Calendar,
  TrendingUp
} from 'lucide-react';

export default function GuideReviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch reviews on mount and filter change
  useEffect(() => {
    fetchReviews(activeFilter);
  }, [activeFilter]);

  const fetchReviews = async (filter) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(`/api/v1/reviews/guide/me?filter=${filter}`, {
        method: 'GET'
      });

      if (response.success) {
        setReviews(response.data.reviews);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setRespondingTo(null);
    setResponseText('');
  };

  const handleStartResponse = (reviewId, existingResponse) => {
    setRespondingTo(reviewId);
    setResponseText(existingResponse || '');
  };

  const handleCancelResponse = () => {
    setRespondingTo(null);
    setResponseText('');
  };

  const handleSubmitResponse = async (reviewId) => {
    if (!responseText.trim()) {
      setError('Please enter a response');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (responseText.length > 500) {
      setError('Response cannot exceed 500 characters');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const method = reviews.find(r => r.reviewId === reviewId)?.guideResponse ? 'PUT' : 'POST';

      const response = await apiRequest(`/api/v1/reviews/${reviewId}/response`, {
        method,
        body: JSON.stringify({ responseText })
      });

      if (response.success) {
        // Refresh reviews list
        await fetchReviews(activeFilter);
        setRespondingTo(null);
        setResponseText('');
      }
    } catch (err) {
      console.error('Error submitting response:', err);
      setError('Failed to submit response. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <_Star
            key={star}
            size={16}
            fill={star <= rating ? 'var(--color-warning)' : 'none'}
            color={star <= rating ? 'var(--color-warning)' : 'var(--color-border)'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filterTabs = [
    { id: 'all', label: 'All Reviews' },
    { id: 'unanswered', label: 'Unanswered' },
    { id: 'answered', label: 'Answered' }
  ];

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="reviews"
      onNavigate={(id, path) => path && navigate(path)}
      user={{
        name: 'Guide User',
        email: 'guide@outdoorguides.com',
        avatar: null
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* Page Header */}
        <div>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)'
          }}>
            My Reviews
          </h1>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            View and respond to customer reviews
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-4)'
          }}>
            <div style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <_Star size={20} color="var(--color-warning)" />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  Average Rating
                </span>
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)' }}>
                {stats.average_rating.toFixed(1)}
              </div>
            </div>

            <div style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <MessageSquare size={20} color="var(--color-primary)" />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  Total Reviews
                </span>
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)' }}>
                {stats.total_reviews}
              </div>
            </div>

            <div style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <TrendingUp size={20} color="var(--color-warning)" />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  Pending Responses
                </span>
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)' }}>
                {stats.pending_responses}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: 'var(--space-2)'
        }}>
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleFilterChange(tab.id)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: activeFilter === tab.id ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
                color: activeFilter === tab.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeFilter === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <LoadingSpinner message="Loading reviews..." />
        )}

        {/* Error State */}
        {error && (
          <Alert variant="error" message={error} />
        )}

        {/* Empty State */}
        {!loading && !error && reviews.length === 0 && (
          <EmptyState
            icon={MessageSquare}
            title="No reviews found"
            description={
              activeFilter === 'all' ? 'You haven\'t received any reviews yet.'
              : activeFilter === 'unanswered' ? 'No unanswered reviews at the moment.'
              : 'No answered reviews to display.'
            }
          />
        )}

        {/* Reviews List */}
        {!loading && !error && reviews.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {reviews.map((review) => (
              <div
                key={review.reviewId}
                style={{
                  padding: 'var(--space-6)',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                {/* Review Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--space-2)'
                    }}>
                      {review.title || 'Review'}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <_User size={14} color="var(--color-text-tertiary)" />
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                          {review.reviewer_first_name} {review.reviewer_last_name}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Calendar size={14} color="var(--color-text-tertiary)" />
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                          {formatDate(review.publishedAt)}
                        </span>
                      </div>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                        {review.experience_title}
                      </span>
                    </div>
                  </div>
                  {renderStars(review.overallRating)}
                </div>

                {/* Review Comment */}
                <p style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--color-text-primary)',
                  lineHeight: '1.6',
                  marginBottom: 'var(--space-4)'
                }}>
                  {review.comment}
                </p>

                {/* Guide Response Section */}
                {review.guideResponse && respondingTo !== review.reviewId && (
                  <div style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--color-background-secondary)',
                    borderLeft: '3px solid var(--color-primary)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: 'var(--space-3)'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--space-2)'
                    }}>
                      Your Response:
                    </p>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                      {review.guideResponse}
                    </p>
                  </div>
                )}

                {/* Response Composer */}
                {respondingTo === review.reviewId ? (
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Write your response... (max 500 characters)"
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: 'var(--space-3)',
                        fontSize: 'var(--font-size-sm)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                      maxLength={500}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'var(--space-2)'
                    }}>
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                        {responseText.length}/500 characters
                      </span>
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                          onClick={handleCancelResponse}
                          style={{
                            padding: 'var(--space-2) var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer'
                          }}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSubmitResponse(review.reviewId)}
                          style={{
                            padding: 'var(--space-2) var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-white)',
                            backgroundColor: 'var(--color-primary)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.6 : 1
                          }}
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit Response'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleStartResponse(review.reviewId, review.guideResponse)}
                    style={{
                      padding: 'var(--space-2) var(--space-4)',
                      fontSize: 'var(--font-size-sm)',
                      color: review.guideResponse ? 'var(--color-text-secondary)' : 'var(--color-primary)',
                      backgroundColor: 'transparent',
                      border: `1px solid ${review.guideResponse ? 'var(--color-border)' : 'var(--color-primary)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer'
                    }}
                  >
                    {review.guideResponse ? 'Edit Response' : 'Respond'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
